import { useEffect, useRef } from 'react';
import type { RefObject } from 'react';

const CSS_VAR = '--top-banner-offset';

const readOffset = (): number => {
  const raw = document.documentElement.style.getPropertyValue(CSS_VAR);
  return Number.parseInt(raw || '0', 10) || 0;
};

const writeOffset = (value: number): void => {
  document.documentElement.style.setProperty(CSS_VAR, `${Math.max(0, value)}px`);
};

const useMeasuredTopBannerRegistration = (
  wrapperRef: RefObject<HTMLElement>,
  active: boolean
): void => {
  const contributedRef = useRef(0);

  useEffect(() => {
    const wrapper = wrapperRef.current;

    const removeContribution = () => {
      if (contributedRef.current !== 0) {
        writeOffset(readOffset() - contributedRef.current);
        contributedRef.current = 0;
      }
    };

    if (!active || !wrapper) {
      removeContribution();
      return undefined;
    }

    // The Banner renders as the wrapper's only child; measuring it (rather than the
    // wrapper) gives the intrinsic content height regardless of the height we write back.
    const bannerEl = wrapper.firstElementChild ?? wrapper;

    let rafId: number | null = null;

    const apply = () => {
      rafId = null;
      const next = Math.round(bannerEl.getBoundingClientRect().height);
      if (next === contributedRef.current) {
        return;
      }
      const delta = next - contributedRef.current;
      writeOffset(readOffset() + delta);
      contributedRef.current = next;
      wrapper.style.height = `${next}px`;
    };

    const schedule = () => {
      rafId ??= requestAnimationFrame(apply);
    };

    apply();

    const observer = new ResizeObserver(schedule);
    observer.observe(bannerEl);

    return () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      observer.disconnect();
      removeContribution();
      wrapper.style.height = '';
    };
  }, [wrapperRef, active]);
};

export default useMeasuredTopBannerRegistration;
